import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  communicationSettingsApi,
  type CtiProvider,
  type StandortCommunicationDto,
} from '../lib/api/communications';

/**
 * Eine Nummer anrufen — mit NFON über die Amtskennziffer des Standorts
 * (Wahlstring in die Zwischenablage, wie im Probestunden-Dialog), sonst über
 * ``tel:``, das die Telefon-App des Rechners öffnet (T-0153).
 *
 * Gibt zurück, was in die Historie gehört, oder ``null``, wenn nichts passiert ist.
 */
export function useAnrufen() {
  const [anbieter, setAnbieter] = useState<CtiProvider>('none');
  const [jeStandort, setJeStandort] = useState<Record<number, StandortCommunicationDto>>({});

  useEffect(() => {
    let abgebrochen = false;
    Promise.all([communicationSettingsApi.get(), communicationSettingsApi.getStandorteMap()])
      .then(([cfg, map]) => {
        if (abgebrochen) return;
        setAnbieter((cfg?.cti?.provider || 'none') as CtiProvider);
        const normiert: Record<number, StandortCommunicationDto> = {};
        Object.entries(map || {}).forEach(([k, v]) => {
          const id = Number(k);
          if (!Number.isNaN(id)) normiert[id] = v;
        });
        setJeStandort(normiert);
      })
      .catch(() => {
        // Ohne Einstellungen (oder ohne Recht darauf) bleibt der Weg über tel:.
      });
    return () => {
      abgebrochen = true;
    };
  }, []);

  return useCallback(
    async (nummer: string, standortId?: number | null): Promise<string | null> => {
      const telefon = nummer.trim();
      if (!telefon) return null;
      if (anbieter === 'nfon') {
        const akz = standortId ? (jeStandort[standortId]?.nfonAkz || '').trim() : '';
        if (!akz) {
          toast.error('NFON-Amtskennziffer ist für diesen Standort nicht konfiguriert');
          return null;
        }
        const wahl = `#${akz} ${telefon}`;
        try {
          await navigator.clipboard.writeText(wahl);
          toast.success('Wahlstring in die Zwischenablage kopiert');
          return `NFON-Wahlstring kopiert: ${wahl}`;
        } catch {
          toast.error('Kopieren in die Zwischenablage fehlgeschlagen');
          return null;
        }
      }
      window.location.href = `tel:${telefon.replace(/\s+/g, '')}`;
      return `Anruf gestartet: ${telefon}`;
    },
    [anbieter, jeStandort],
  );
}
